import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, clasesService, cursosService } from '../services/api';
import HtmlContent from '../components/HtmlContent';
import Pagination from '../components/Pagination';
import ConfirmPopup from '../components/ConfirmPopup';
import './Clases.css';

const getYoutubeEmbedUrl = (url) => {
  const s = String(url || '').trim();
  const match = s.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

const Clases = () => {
  const navigate = useNavigate();
  const [clases, setClases] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCursoId, setSelectedCursoId] = useState('');
  const [mostrarCursosInactivos, setMostrarCursosInactivos] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isProfesor, setIsProfesor] = useState(false);
  const [isCoordinador, setIsCoordinador] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedMaterialRows, setExpandedMaterialRows] = useState({});
  const [confirmEliminar, setConfirmEliminar] = useState({ show: false, clase: null });
  const [previewClase, setPreviewClase] = useState(null);
  const itemsPerPage = 10;
  useEffect(() => {
    // Verificar autenticación
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    const user = authService.getCurrentUser();
    setCurrentUser(user);

    const savedRole = localStorage.getItem('selectedRole');
    if (savedRole) {
      setSelectedRole(savedRole);
      setIsProfesor(savedRole === 'Profesor');
      setIsCoordinador(savedRole === 'Coordinador');
    } else if (user && user.groups) {
      const grupos = Array.isArray(user.groups) ? user.groups : [user.groups];
      const primerRol = typeof grupos[0] === 'string' ? grupos[0] : (grupos[0].name || String(grupos[0]));
      setSelectedRole(primerRol);
      setIsProfesor(primerRol === 'Profesor');
      setIsCoordinador(primerRol === 'Coordinador');
      localStorage.setItem('selectedRole', primerRol);
    }

    loadClases();
    loadCursos();
  }, [navigate]);

  useEffect(() => {
    if (currentUser !== null) {
      loadCursos();
    }
  }, [currentUser, isProfesor, isCoordinador]);

  const cursosParaSelector = mostrarCursosInactivos
    ? (cursos || []).filter(c => c.activo === false)
    : (cursos || []).filter(c => c.activo !== false);

  useEffect(() => {
    const list = mostrarCursosInactivos ? (cursos || []).filter(c => c.activo === false) : (cursos || []).filter(c => c.activo !== false);
    if (selectedCursoId && !list.some(c => String(c.id) === String(selectedCursoId))) {
      setSelectedCursoId('');
    }
  }, [mostrarCursosInactivos, cursos, selectedCursoId]);

  useEffect(() => {
    const handleRoleChange = (event) => {
      const newRole = event.detail?.role || localStorage.getItem('selectedRole');
      if (newRole && newRole !== selectedRole) {
        setSelectedRole(newRole);
        setIsProfesor(newRole === 'Profesor');
        setIsCoordinador(newRole === 'Coordinador');
      }
    };
    window.addEventListener('roleChanged', handleRoleChange);
    window.addEventListener('storage', handleRoleChange);
    return () => {
      window.removeEventListener('roleChanged', handleRoleChange);
      window.removeEventListener('storage', handleRoleChange);
    };
  }, [selectedRole]);

  const loadCursos = async () => {
    try {
      const data = await cursosService.getCursos();
      // Solo mostrar cursos a los que el usuario está vinculado (en el equipo)
      if (isProfesor && !isCoordinador && currentUser) {
        const cursosFiltrados = (data || []).filter(curso => {
          if (curso.equipo && Array.isArray(curso.equipo)) {
            return curso.equipo.some(miembroId => {
              const id = typeof miembroId === 'object' && miembroId.id ? miembroId.id : miembroId;
              return parseInt(id) === parseInt(currentUser.id);
            });
          }
          return false;
        });
        setCursos(cursosFiltrados);
      } else {
        setCursos(data || []);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al cargar cursos';
      setError(msg);
      setCursos([]);
      console.error('Error al cargar cursos:', err);
    }
  };

  const loadClases = async () => {
    try {
      setLoading(true);
      const data = await clasesService.getClases();
      setClases(data);
    } catch (err) {
      setError('Error al cargar clases');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePublicar = async (claseId) => {
    try {
      await clasesService.publicarClase(claseId);
      loadClases();
    } catch (err) {
      setError('Error al publicar la clase');
      console.error(err);
    }
  };

  const handleDelete = (clase) => {
    setConfirmEliminar({ show: true, clase });
  };

  const confirmarEliminarClase = async () => {
    const clase = confirmEliminar.clase;
    setConfirmEliminar({ show: false, clase: null });
    if (!clase?.id) return;
    try {
      await clasesService.deleteClase(clase.id);
      loadClases();
    } catch (err) {
      setError('Error al eliminar la clase');
      console.error(err);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-AR');
  };

  const getTipoMaterialLabel = (tipo) => {
    const labels = {
      'archivo': '📎 Archivo',
      'enlace': '🔗 Enlace',
      'codigo': '💻 Código',
      'texto': '📝 Texto',
      'video': '🎬 Video'
    };
    return labels[tipo] || tipo;
  };

  const toggleMaterialRow = (claseId) => {
    setExpandedMaterialRows((prev) => ({
      ...prev,
      [claseId]: !prev[claseId],
    }));
  };

  const clasesFiltradas = selectedCursoId
    ? (clases || []).filter(c => c.curso === parseInt(selectedCursoId, 10))
    : [];

  const totalPages = Math.ceil(clasesFiltradas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const clasesPaginadas = clasesFiltradas.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCursoId]);

  if (loading && clases.length === 0) {
    return (
      <div className="clases-loading">
        <div className="loading-spinner"></div>
        <p>Cargando clases...</p>
      </div>
    );
  }

  return (
    <div className="clases-container">
      <div className="clases-header">
        <h1>Gestión de Clases</h1>
        <div className="clases-actions">
          <button
            className="clases-btn clases-btn-primary"
            onClick={() => navigate('/dashboard/clases/nuevo')}
          >
            ➕ Nueva Clase
          </button>
        </div>
      </div>

      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />

      <div className="clases-curso-selector">
        <div className="clases-form-group">
          <label htmlFor="selector-curso">Curso</label>
          <select
            id="selector-curso"
            className="clases-select-curso"
            value={selectedCursoId}
            onChange={(e) => setSelectedCursoId(e.target.value || '')}
          >
            <option value="">Seleccionar curso</option>
            {cursosParaSelector.map((curso) => (
              <option key={curso.id} value={curso.id}>
                {curso.nombre}
                {curso.activo === false ? ' (inactivo)' : ''}
              </option>
            ))}
          </select>
        </div>
        <label className="clases-checkbox-label clases-checkbox-inactivos">
          <input
            type="checkbox"
            checked={mostrarCursosInactivos}
            onChange={(e) => setMostrarCursosInactivos(e.target.checked)}
          />
          <span>Mostrar solo cursos inactivos</span>
        </label>
      </div>

      {!selectedCursoId ? (
        <div className="clases-sin-curso">
          <p>Seleccione un curso en el desplegable para ver las clases.</p>
        </div>
      ) : (
      <div className="clases-table-container">
        <table className="clases-table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Descripción</th>
              <th>Curso</th>
              <th>Fecha Programada</th>
              <th>Fecha Publicación</th>
              <th>Estado</th>
              <th>Material</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {clasesFiltradas.length === 0 ? (
              <tr>
                <td colSpan="8" className="clases-empty">
                  No hay clases para este curso
                </td>
              </tr>
            ) : (
              clasesPaginadas.map((clase) => (
                <tr key={clase.id}>
                  <td>{clase.titulo || '-'}</td>
                  <td className="clases-descripcion">
                    {clase.descripcion ? (
                      <HtmlContent content={clase.descripcion} maxLength={50} className="clases-descripcion-text" />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{clase.curso_nombre || '-'}</td>
                  <td>{formatDateTime(clase.fecha_programada)}</td>
                  <td>{formatDateTime(clase.fecha_publicacion)}</td>
                  <td>
                    <span className={`clases-status ${clase.publicada ? 'publicada' : 'no-publicada'}`}>
                      {clase.publicada ? 'Publicada' : 'No Publicada'}
                    </span>
                  </td>
                  <td>
                    {clase.material && clase.material.length > 0 ? (
                      <div className="clases-material">
                        {(expandedMaterialRows[clase.id] ? clase.material : [clase.material[0]]).map((mat, index) => (
                          <span key={index} className="clases-material-badge">
                            {getTipoMaterialLabel(mat.tipo)}
                          </span>
                        ))}
                        {clase.material.length > 1 && (
                          <button
                            type="button"
                            className="clases-material-ver-mas"
                            onClick={() => toggleMaterialRow(clase.id)}
                          >
                            {expandedMaterialRows[clase.id]
                              ? 'Ver menos'
                              : `Ver más (${clase.material.length - 1})`}
                          </button>
                        )}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <div className="clases-actions-cell">
                      <button
                        type="button"
                        className="clases-btn-action clases-btn-preview"
                        onClick={() => setPreviewClase(clase)}
                        title="Previsualizar"
                        aria-label={`Previsualizar ${clase.titulo || 'clase'}`}
                      >
                        👁️
                      </button>
                      {!clase.publicada && (
                        <button
                          className="clases-btn-action clases-btn-publicar"
                          onClick={() => handlePublicar(clase.id)}
                          title="Publicar ahora"
                        >
                          📢
                        </button>
                      )}
                      <button
                        className="clases-btn-action clases-btn-edit"
                        onClick={() => navigate(`/dashboard/clases/${clase.id}/editar`)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        className="clases-btn-action clases-btn-delete"
                        onClick={() => handleDelete(clase)}
                        title="Eliminar"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
      {clasesFiltradas.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {previewClase && (
        <div
          className="clases-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreviewClase(null);
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="clases-preview-title"
        >
          <div className="clases-modal clases-preview-modal">
            <div className="clases-modal-header">
              <h2 id="clases-preview-title">Vista previa</h2>
              <button
                type="button"
                className="clases-modal-close"
                onClick={() => setPreviewClase(null)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="clases-preview-modal-body">
              <h3 className="clases-preview-titulo">{previewClase.titulo || 'Clase'}</h3>
              {previewClase.curso_nombre && (
                <p className="clases-preview-meta"><strong>Curso:</strong> {previewClase.curso_nombre}</p>
              )}
              <p className="clases-preview-meta">
                <strong>Estado:</strong> {previewClase.publicada ? 'Publicada' : 'No publicada'}
              </p>
              {previewClase.descripcion ? (
                <div className="clases-preview-desc">
                  <HtmlContent content={previewClase.descripcion} tag="div" />
                </div>
              ) : (
                <p className="clases-preview-sin-desc">Sin descripción</p>
              )}
              {Array.isArray(previewClase.material) && previewClase.material.length > 0 && (
                <div className="clases-preview-material">
                  <strong>Material</strong>
                  {previewClase.material.map((mat, idx) => {
                    const contenido = mat.contenido || '';
                    const tipo = (mat.tipo || '').toLowerCase();
                    const esUrl = /^https?:\/\//i.test(String(contenido).trim());
                    if (tipo === 'codigo') {
                      return (
                        <div key={idx} className="clases-preview-material-item">
                          <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
                          <pre className="clases-preview-codigo"><code>{contenido}</code></pre>
                        </div>
                      );
                    }
                    if (tipo === 'video') {
                      const embedUrl = getYoutubeEmbedUrl(contenido);
                      return (
                        <div key={idx} className="clases-preview-material-item">
                          <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
                          {embedUrl ? (
                            <div className="clases-preview-video-wrap">
                              <iframe title="Vista previa video" src={embedUrl} className="clases-preview-video" allowFullScreen />
                            </div>
                          ) : (
                            <a href={contenido.trim()} target="_blank" rel="noopener noreferrer" className="clases-preview-enlace">Ver video: {contenido.trim()}</a>
                          )}
                        </div>
                      );
                    }
                    if (tipo === 'enlace' && esUrl) {
                      const textoVisible = (mat.titulo || '').trim() || contenido.trim();
                      return (
                        <div key={idx} className="clases-preview-material-item">
                          <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
                          <a href={contenido.trim()} target="_blank" rel="noopener noreferrer" className="clases-preview-enlace" title={contenido.trim()}>{textoVisible}</a>
                        </div>
                      );
                    }
                    if (tipo === 'archivo') {
                      return (
                        <div key={idx} className="clases-preview-material-item clases-preview-archivo">
                          <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
                          {esUrl ? (
                            <a href={contenido.trim()} target="_blank" rel="noopener noreferrer">📎 {mat.titulo || 'Archivo'}</a>
                          ) : (
                            <span className="clases-preview-archivo-nombre">📎 {contenido || 'Archivo'}</span>
                          )}
                        </div>
                      );
                    }
                    if (tipo === 'texto' && /<[a-z][\s\S]*>/i.test(contenido)) {
                      return (
                        <div key={idx} className="clases-preview-material-item">
                          <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
                          <div className="clases-preview-texto"><HtmlContent content={contenido} tag="div" /></div>
                        </div>
                      );
                    }
                    return (
                      <div key={idx} className="clases-preview-material-item">
                        <span className="clases-preview-material-badge">{getTipoMaterialLabel(mat.tipo)}</span>
                        <div className="clases-preview-texto">{contenido || '—'}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmPopup
        open={confirmEliminar.show}
        title="Eliminar clase"
        message={
          confirmEliminar.clase?.titulo
            ? `¿Estás seguro de que deseás eliminar la clase «${confirmEliminar.clase.titulo}»?`
            : '¿Estás seguro de que deseás eliminar esta clase?'
        }
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmarEliminarClase}
        onCancel={() => setConfirmEliminar({ show: false, clase: null })}
      />
    </div>
  );
};

export default Clases;
